// Mock data for BIZSOFT Consulting website

export const mockFormSubmission = async (formData) => {
  // Simulate API call
  return new Promise((resolve) => {
    setTimeout(() => {
      console.log('Form submitted:', formData);
      resolve({
        success: true,
        message: 'Thank you for your interest! We will contact you shortly.'
      });
    }, 1000);
  });
};

export const heroContent = {
  headline: "Stop renting your software. Own it.",
  subheadline: "Spending $5K+ per month on SaaS? We'll build you a custom system you own in 6 months—using your current SaaS budget for 6 months as the build budget.",
  disclaimer: "For qualifying projects based on scope assessment",
  ctaPrimary: "Get Your Free SaaS Audit",
  ctaSecondary: "30-minute consultation. No commitment.",
  heroImage: "https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHx0ZWNobm9sb2d5JTIwYWJzdHJhY3R8ZW58MHx8fHwxNzcwNTQyODI3fDA&ixlib=rb-4.1.0&q=85"
};

export const costExamples = [
  {
    title: "Small Business Example",
    monthlySaaS: 8000,
    buildMonths: 6,
    totalBuild: 50000,
    maintenanceMonthly: 1200,
    annualSavings: 82000
  },
  {
    title: "Mid-Market Example",
    monthlySaaS: 15000,
    buildMonths: 6,
    totalBuild: 90000,
    maintenanceMonthly: 2250,
    annualSavings: 153000
  }
];

export const projectFit = {
  title: "Is Your Project a Good Fit?",
  subtitle: "Our 6-month model works best for:",
  criteria: [
    {
      title: "Clear Scope",
      description: "You know what features you need and use regularly",
      icon: "Target"
    },
    {
      title: "Standard Integrations",
      description: "Common APIs and data sources (not highly specialized legacy systems)",
      icon: "Plug"
    },
    {
      title: "Established Workflows",
      description: "Your processes are documented and stable",
      icon: "GitBranch"
    },
    {
      title: "Reasonable Complexity",
      description: "Projects under $100K total build cost typically fit our timeline",
      icon: "Gauge"
    }
  ],
  disclaimer: "Complex projects with extensive custom integrations or enterprise-scale requirements may require adjusted timelines and pricing, which we'll discuss transparently during your free consultation."
};

export const howItWorks = [
  {
    step: 1,
    title: "Free SaaS Audit & Scope Assessment",
    description: "We review your tools, costs, and workflows. You get a savings estimate, project feasibility assessment, and 6-month plan (if your project qualifies).",
    icon: "Search"
  },
  {
    step: 2,
    title: "6-Month Build",
    description: "You pay what you already pay in SaaS. No increase in monthly spend. We build a system with only the features you use.",
    icon: "Code"
  },
  {
    step: 3,
    title: "You Own the System",
    description: "After 6 months: No SaaS subscriptions, no vendor lock-in, full control of your software. Maintenance: Just 15% of your original SaaS spend.",
    icon: "CheckCircle"
  }
];

export const targetAudience = {
  title: "Built for companies spending $5K+ per month on SaaS",
  points: [
    "Use only part of their SaaS features",
    "Want more control over systems",
    "Tired of rising subscription costs",
    "Need tailored workflows"
  ],
  exampleSystems: [
    "CRM",
    "Operations platforms",
    "Scheduling systems",
    "Internal dashboards",
    "Industry-specific tools"
  ]
};

export const whyBizsoft = {
  title: "Why OwnStack",
  points: [
    "Predictable 6-month build model",
    "Pricing tied to your current SaaS spend",
    "No long-term SaaS lock-in",
    "Designed for AI-assisted workflows",
    "Ongoing support available"
  ]
};

export const testimonials = [
  {
    firstName: "Marcus",
    industry: "Manufacturing",
    systemReplaced: "Salesforce CRM",
    quote: "We were spending $12K/month on Salesforce and only using 30% of features. OwnStack built us exactly what we needed. ROI achieved in 8 months."
  },
  {
    firstName: "Sarah",
    industry: "Professional Services",
    systemReplaced: "Multiple SaaS Tools",
    quote: "Replaced 5 different SaaS subscriptions with one integrated system. Our team actually uses it, and we're saving $95K annually."
  },
  {
    firstName: "David",
    industry: "Healthcare",
    systemReplaced: "Custom ERP System",
    quote: "Transitioned from an outdated ERP costing $18K/month. Now we have full control, better features, and pay only $2,700/month in maintenance."
  }
];

export const finalCTA = {
  headline: "Get your SaaS Exit Plan",
  description: "Free 30-minute consultation:",
  points: [
    "Review your SaaS stack",
    "Estimate savings",
    "Outline a 6-month replacement plan"
  ],
  ctaButton: "Book Your Free Audit",
  socialProof: "Join the growing number of companies who made the switch from SaaS to ownership"
};

export const footer = {
  companyName: "OwnStack",
  tagline: "Turn your SaaS bill into your build budget.",
  email: "hello@ownstack.software",
  copyright: `© ${new Date().getFullYear()} OwnStack. All rights reserved.`,
  disclaimer: "6-month timeline applies to projects meeting our scope criteria. Complex integrations may require adjusted timelines, discussed during consultation."
};

export const faqData = {
  title: "Frequently Asked Questions",
  questions: [
    {
      question: "What happens to our data during migration?",
      answer: "We implement a secure, phased migration strategy. Your data remains accessible in your current SaaS throughout the build. We run parallel systems during transition, ensuring zero data loss and minimal disruption. You maintain full control and ownership of all data."
    },
    {
      question: "Do you provide training for our team?",
      answer: "Yes. Training is included in every project. We provide comprehensive onboarding documentation, video tutorials, and live training sessions. We also offer ongoing support during the first 30 days post-launch to ensure your team is comfortable with the new system."
    },
    {
      question: "You mentioned AI coding tools—does that mean lower quality?",
      answer: "Quite the opposite. AI tools handle repetitive, boilerplate code that's prone to human error, while our experienced developers focus on your unique business logic, architecture decisions, and quality assurance. Think of it like modern construction: power tools make builders faster and more precise, but you still need skilled craftspeople. The result is higher quality, delivered faster, at a better price point."
    },
    {
      question: "What kind of ongoing support do you offer?",
      answer: "After launch, we offer flexible maintenance plans starting at 15% of your original SaaS spend. This includes bug fixes, security updates, minor feature adjustments, and technical support. We're also available for major enhancements as your business evolves."
    },
    {
      question: "How do you handle security and compliance?",
      answer: "Security is built into every phase. We follow industry best practices including encrypted data storage, secure authentication, regular security audits, and compliance with relevant regulations (GDPR, HIPAA, SOC 2 depending on your industry). Your system can be hosted on your preferred secure infrastructure."
    },
    {
      question: "What if we need to scale beyond the original scope?",
      answer: "We build with scalability in mind. Your system is designed to grow with your business. Future enhancements are priced transparently based on scope, and we maintain detailed documentation making it easy to add features or even transition to another development team if needed."
    },
    {
      question: "Can we start small and expand later?",
      answer: "Absolutely. We recommend starting with core functionality that delivers immediate ROI, then expanding based on your priorities. This phased approach reduces risk and lets you validate the system before committing to additional features."
    }
  ]
};

export const comparisonData = {
  title: "Why Not Just Hire Developers?",
  subtitle: "We've thought about this too. Here's an honest comparison:",
  options: [
    {
      title: "Hire In-House Team",
      pros: [
        "Full control over team",
        "Deep company knowledge",
        "Available for ongoing work"
      ],
      cons: [
        "6-12 months to hire & onboard",
        "Annual cost: $300K+ (Sr Dev + benefits)",
        "Management overhead",
        "Single point of failure",
        "Limited skillset diversity"
      ],
      timeline: "12+ months",
      cost: "$300K+/year",
      risk: "High"
    },
    {
      title: "Hire Freelancers",
      pros: [
        "Lower hourly rates",
        "Flexible engagement",
        "Specialized skills"
      ],
      cons: [
        "Quality varies widely",
        "No accountability",
        "Communication challenges",
        "Knowledge loss when they leave",
        "Project management falls on you"
      ],
      timeline: "8-14 months",
      cost: "$60K-120K",
      risk: "Very High"
    },
    {
      title: "OwnStack",
      pros: [
        "Proven 6-month delivery model",
        "Full-stack team included",
        "Fixed monthly cost (your SaaS spend)",
        "Project management included",
        "Quality guaranteed",
        "Ongoing support available"
      ],
      cons: [
        "Not your employees",
        "May require some adjustment period"
      ],
      timeline: "6 months",
      cost: "= Your SaaS spend",
      risk: "Low"
    }
  ],
  conclusion: "With OwnStack, you get the quality of in-house development, the speed of a focused team, and the cost predictability you need—without the hiring headaches.",
  aiAdvantage: {
    title: "Our Secret: AI-Powered Development",
    description: "We're able to deliver solutions faster and more cost-effectively because we embrace cutting-edge AI coding tools. This means:",
    benefits: [
      "Faster development cycles without sacrificing quality",
      "Reduced boilerplate and repetitive coding work",
      "More time focused on your unique business logic",
      "Lower costs passed directly to you",
      "Consistent code quality and best practices"
    ],
    note: "We combine AI efficiency with human expertise—AI handles the routine, our developers focus on what makes your business unique."
  }
};
